import { useNuxtApp } from '#app'
import { getMallAppToken, getToken } from '@/utils/globalData'
import { generateEventId, getMetaBrowserTracking } from '@/utils/eventTracking'
import {fixFbParams} from "@/api/mall"

/**
 * Analytics utility supporting Google Analytics, TikTok Pixel, and Facebook Pixel
 *
 * Features:
 * - Automatic SDK initialization checking for TikTok and Facebook Pixel
 * - Event queuing with timeout for SDK readiness
 * - User identification support for all platforms
 * - Backward compatibility with Google Analytics
 *
 * Usage Examples:
 *
 * // ViewContent event for all platforms
 * trackEvent('ViewContent', {
 *   contents: [{ content_id: '123', content_name: 'Product Name' }],
 *   value: 99.99,
 *   currency: 'USD'
 * });
 *
 * // AddToCart with user identification
 * trackEvent('AddToCart', {
 *   contents: [{ content_id: '123', content_name: 'Product Name' }],
 *   value: 99.99,
 *   currency: 'USD',
 *   userData: { email: 'hashed_email@example.com' }
 * });
 *
 * // Search event
 * trackEvent('Search', {
 *   contents: [{ content_id: '123', content_name: 'Search Result' }],
 *   value: 0,
 *   currency: 'USD',
 *   search_string: 'red shoes'
 * });
 *
 * // Backward compatibility with Google Analytics
 * trackEvent('ecommerce', {
 *   category: 'ecommerce',
 *   action: 'purchase',
 *   label: 'Product Purchase',
 *   value: 99.99
 * });
 */

// Check if TikTok Pixel SDK is ready
function isTikTokReady() {
  return process.client &&
         window.ttq &&
         typeof window.ttq.track === 'function' &&
         typeof window.ttq.identify === 'function';
}

// Check if Facebook Pixel SDK is ready
function isFacebookReady() {
  return process.client &&
         window.fbq &&
         typeof window.fbq === 'function';
}

// Wait for TikTok SDK to be ready with timeout
function waitForTikTokReady(timeout = 5000) {
  return new Promise((resolve) => {
    if (isTikTokReady()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkReady = () => {
      if (isTikTokReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        console.warn('TikTok SDK initialization timeout');
        resolve(false);
        return;
      }

      setTimeout(checkReady, 100);
    };

    checkReady();
  });
}

// Wait for Facebook Pixel SDK to be ready with timeout
function waitForFacebookReady(timeout = 5000) {
  return new Promise((resolve) => {
    if (isFacebookReady()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkReady = () => {
      if (isFacebookReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        console.warn('Facebook Pixel SDK initialization timeout');
        resolve(false);
        return;
      }

      setTimeout(checkReady, 100);
    };

    checkReady();
  });
}

// TikTok Pixel event tracking
async function trackTikTokEvent(eventName, parameters = {}, options = {}) {
  try {
    // Wait for TikTok SDK to be ready
    const isReady = await waitForTikTokReady();

    if (!isReady) {
      console.warn('TikTok SDK not ready, skipping event:', eventName);
      return;
    }

    if (options.eventId) {
      window.ttq.track(eventName, parameters, {
        event_id: options.eventId
      });
      return;
    }

    window.ttq.track(eventName, parameters);
  } catch (e) {
    console.warn('TikTok tracking error:', e);
  }
}

// Facebook Pixel event tracking
async function trackFacebookEvent(eventName, parameters = {}, options = {}) {
  try {
    const purchaseContext = buildFacebookPurchaseContext({
      eventName,
      eventId: options.eventId,
      userData: options.userData,
      value: parameters?.value,
      currency: parameters?.currency
    })

    if (purchaseContext.isDuplicate) {
      console.warn('Facebook Pixel Purchase already sent, skipping duplicate:', purchaseContext.dedupKey)
      return
    }

    // Wait for Facebook Pixel SDK to be ready
    const isReady = await waitForFacebookReady();

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping event:', eventName);
      return;
    }

    if (options.eventId) {
      window.fbq('track', eventName, parameters, {
        eventID: options.eventId
      });
      if (eventName === 'Purchase') {
        markFacebookPurchaseEventSent(purchaseContext.dedupKey)
      }
      return;
    }

    window.fbq('track', eventName, parameters);
    if (eventName === 'Purchase') {
      markFacebookPurchaseEventSent(purchaseContext.dedupKey)
    }
  } catch (e) {
    console.warn('Facebook tracking error:', e);
  }
}

// TikTok user identification
async function identifyTikTokUser(userData = {}) {
  try {
    // Wait for TikTok SDK to be ready
    const isReady = await waitForTikTokReady();

    if (!isReady) {
      console.warn('TikTok SDK not ready, skipping user identification');
      return;
    }

    const identifyData = {};

    if (userData.email) {
      identifyData.email = userData.email; // Should be SHA-256 hashed
    }
    if (userData.phone_number) {
      identifyData.phone_number = userData.phone_number; // Should be SHA-256 hashed
    }
    if (userData.external_id) {
      identifyData.external_id = userData.external_id; // Should be SHA-256 hashed
    }

    if (Object.keys(identifyData).length > 0) {
      window.ttq.identify(identifyData);
    }
  } catch (e) {
    console.warn('TikTok identify error:', e);
  }
}

// Facebook user identification
async function identifyFacebookUser(userData = {}) {
  try {
    // Wait for Facebook Pixel SDK to be ready
    const isReady = await waitForFacebookReady();

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping user identification');
      return;
    }

    const identifyData = {};

    if (userData.email) {
      identifyData.em = userData.email; // Should be SHA-256 hashed
    }
    if (userData.phone_number) {
      identifyData.ph = userData.phone_number; // Should be SHA-256 hashed
    }
    if (userData.first_name) {
      identifyData.fn = userData.first_name; // Should be SHA-256 hashed
    }
    if (userData.last_name) {
      identifyData.ln = userData.last_name; // Should be SHA-256 hashed
    }
    if (userData.external_id) {
      identifyData.external_id = userData.external_id; // Should be SHA-256 hashed
    }

    if (Object.keys(identifyData).length > 0) {
      // Facebook Pixel uses userData in event parameters or setUserData
      window.fbq('set', 'userData', identifyData);
    }
  } catch (e) {
    console.warn('Facebook identify error:', e);
  }
}

// Google Analytics event tracking (backward compatibility)
function trackGoogleAnalytics(category, action, label = "", value = 0) {
  try {
    if (process.client && window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  } catch (e) {}
}

/**
 * Clean and normalize value to ensure it's a valid number
 * TikTok requires value to be a number (integer or decimal) without currency symbols, letters, special characters, or commas
 * @param {any} value - The value to clean (can be string, number, etc.)
 * @returns {number} - A valid number (defaults to 0 if invalid)
 */
function normalizeValue(value) {
  // If value is already a valid number, return it
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }

  // If value is undefined, null, or empty, return 0
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  // Convert to string and remove all non-numeric characters except decimal point
  // This removes currency symbols, commas, letters, and other special characters
  let cleanedValue = String(value)
    .replace(/,/g, '') // Remove commas (thousand separators) first
    .replace(/[^\d.]/g, '') // Remove everything except digits and decimal point
    .replace(/\.{2,}/g, '.') // Replace multiple decimal points with single decimal point
    .replace(/^\./, '0.') // Add leading zero if starts with decimal point
    .replace(/\.$/, '') // Remove trailing decimal point
    || '0'; // Default to '0' if empty after cleaning

  // Convert to number
  const numValue = parseFloat(cleanedValue);

  // Return valid number or 0 as fallback
  return isNaN(numValue) || !isFinite(numValue) ? 0 : numValue;
}

function normalizePlatformProperties(properties = {}) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return {}
  }

  return Object.entries(properties).reduce((result, [key, value]) => {
    if (value === undefined || value === null || value === '') {
      return result
    }

    result[key] = value
    return result
  }, {})
}

function getCookieValue(key) {
  if (!process.client || !key) {
    return ''
  }

  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${key}=([^;]*)`))
    return match?.[1] ? decodeURIComponent(match[1]) : ''
  } catch (error) {
    return ''
  }
}

function normalizeEventStringValue(value) {
  if (value === undefined || value === null) {
    return ''
  }

  return `${value}`.trim()
}

function resolveFacebookShopId() {
  return getCookieValue('dealerShopId')
}

function resolveFacebookUserId(userData = {}) {
  return normalizeEventStringValue(
    userData.user_id
    || userData.userId
    || userData.external_id
    || userData.externalId
    || resolveFacebookShopId()
  )
}

function buildFacebookEventSourceUrl() {
  if (!process.client || typeof window === 'undefined') {
    return ''
  }

  return fixFbParams(window.location.href)
}

const FACEBOOK_PURCHASE_DEDUP_STORAGE_KEY = 'facebook_purchase_event_dedup'
const FACEBOOK_PURCHASE_DEDUP_MAX_SIZE = 10

function getFacebookPurchaseDedupList() {
  if (!process.client) {
    return []
  }

  try {
    const parsed = JSON.parse(localStorage.getItem(FACEBOOK_PURCHASE_DEDUP_STORAGE_KEY) || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

function buildFacebookPurchaseDedupKey({ eventId, userData, value, currency }) {
  const normalizedEventId = normalizeEventStringValue(eventId)
  const normalizedExternalId = normalizeEventStringValue(userData?.external_id || userData?.externalId || userData?.user_id || userData?.userId)
  const normalizedValue = normalizeEventStringValue(value)
  const normalizedCurrency = normalizeEventStringValue(currency)

  return [normalizedEventId, normalizedExternalId, normalizedValue, normalizedCurrency]
    .filter(Boolean)
    .join('::')
}

function hasSentFacebookPurchaseEvent(dedupKey) {
  if (!dedupKey) {
    return false
  }

  return getFacebookPurchaseDedupList().includes(dedupKey)
}

function markFacebookPurchaseEventSent(dedupKey) {
  if (!process.client || !dedupKey) {
    return
  }

  try {
    const nextList = [dedupKey, ...getFacebookPurchaseDedupList().filter(item => item !== dedupKey)]
      .slice(0, FACEBOOK_PURCHASE_DEDUP_MAX_SIZE)

    localStorage.setItem(FACEBOOK_PURCHASE_DEDUP_STORAGE_KEY, JSON.stringify(nextList))
  } catch (error) {
    // Ignore localStorage failures to avoid blocking checkout success flow.
  }
}

function buildFacebookPurchaseContext({ eventName, eventId, userData, value, currency }) {
  if (eventName !== 'Purchase') {
    return {
      dedupKey: '',
      isDuplicate: false
    }
  }

  const dedupKey = buildFacebookPurchaseDedupKey({ eventId, userData, value, currency })

  return {
    dedupKey,
    isDuplicate: hasSentFacebookPurchaseEvent(dedupKey)
  }
}

async function sha256Hex(value) {
  const normalizedValue = normalizeEventStringValue(value)
  if (!normalizedValue) {
    return ''
  }

  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(normalizedValue)
    const digest = await window.crypto.subtle.digest('SHA-256', data)

    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch (error) {
    return normalizedValue
  }
}

function normalizeFacebookServerUserData(userData = {}) {
  if (!userData || typeof userData !== 'object' || Array.isArray(userData)) {
    return undefined
  }

  const normalizedUserData = {}

  if (userData.external_id || userData.externalId) {
    normalizedUserData.externalId = userData.external_id || userData.externalId
  }
  if (userData.email || userData.em) {
    normalizedUserData.email = userData.email || userData.em
  }
  if (userData.phone_number || userData.phone || userData.ph) {
    normalizedUserData.phone = userData.phone_number || userData.phone || userData.ph
  }
  if (userData.city || userData.ct) {
    normalizedUserData.city = userData.city || userData.ct
  }
  if (userData.state || userData.st) {
    normalizedUserData.state = userData.state || userData.st
  }
  if (userData.zip || userData.zp) {
    normalizedUserData.zip = userData.zip || userData.zp
  }
  if (userData.country) {
    normalizedUserData.country = userData.country
  }
  if (userData.client_ip_address || userData.clientIpAddress) {
    normalizedUserData.clientIpAddress = userData.client_ip_address || userData.clientIpAddress
  }
  if (userData.client_user_agent || userData.clientUserAgent) {
    normalizedUserData.clientUserAgent = userData.client_user_agent || userData.clientUserAgent
  }
  if (userData.fbp) {
    normalizedUserData.fbp = userData.fbp
  } else {
    normalizedUserData.fbp = getCookieValue('_fbp')
  }
  if (userData.fbc) {
    normalizedUserData.fbc = userData.fbc
  } else {
    normalizedUserData.fbc = getCookieValue('_fbc')
  }

  return Object.keys(normalizedUserData).length > 0 ? normalizedUserData : undefined
}

async function buildFacebookServerUserData(userData = {}) {
  const normalizedUserData = normalizeFacebookServerUserData(userData) || {}
  const browserTracking = process.client ? getMetaBrowserTracking() : {}
  const payload = {}

  const fbp = normalizeEventStringValue(normalizedUserData.fbp || browserTracking.fbp)
  if (fbp) {
    payload.fbp = fbp
  }

  const fbc = normalizeEventStringValue(normalizedUserData.fbc || browserTracking.fbc)
  if (fbc) {
    payload.fbc = fbc
  }

  const externalId = await sha256Hex(normalizedUserData.externalId)
  if (externalId) {
    payload.externalId = externalId
  }

  const clientIpAddress = normalizeEventStringValue(normalizedUserData.clientIpAddress)
  if (clientIpAddress) {
    payload.clientIpAddress = clientIpAddress
  }

  const clientUserAgent = normalizeEventStringValue(
    normalizedUserData.clientUserAgent || (process.client ? window.navigator.userAgent : '')
  )
  if (clientUserAgent) {
    payload.clientUserAgent = clientUserAgent
  }

  const em = await sha256Hex(normalizedUserData.email && normalizeEventStringValue(normalizedUserData.email).toLowerCase())
  if (em) {
    payload.em = [em]
  }

  const ph = await sha256Hex(normalizedUserData.phone && normalizeEventStringValue(normalizedUserData.phone).replace(/(?!^\+)[^\d]/g, ''))
  if (ph) {
    payload.ph = [ph]
  }

  const ct = await sha256Hex(normalizedUserData.city && normalizeEventStringValue(normalizedUserData.city).toLowerCase())
  if (ct) {
    payload.ct = [ct]
  }

  const st = await sha256Hex(normalizedUserData.state && normalizeEventStringValue(normalizedUserData.state).toLowerCase())
  if (st) {
    payload.st = [st]
  }

  const zp = await sha256Hex(normalizedUserData.zip)
  if (zp) {
    payload.zp = [zp]
  }

  const country = await sha256Hex(normalizedUserData.country && normalizeEventStringValue(normalizedUserData.country).toLowerCase())
  if (country) {
    payload.country = [country]
  }

  return Object.keys(payload).length > 0 ? payload : undefined
}

function buildFacebookServerCustomData(eventType, contents = [], value, currency, facebookProperties = {}) {
  const contentIds = contents
    .map(content => content.content_id || content.id)
    .filter(Boolean)
    .map(item => `${item}`)

  const contentName = facebookProperties.contentName
    || facebookProperties.content_name
    || (contents.length > 0 ? (contents[0].content_name || contents[0].name) : undefined)

  const contentCategory = facebookProperties.contentCategory
    || facebookProperties.content_category
    || (contents.length > 0 ? (contents[0].content_category || contents[0].category) : undefined)

  const customData = {
    value: normalizeValue(value),
    currency,
    contentIds,
    contentType: facebookProperties.contentType || facebookProperties.content_type || 'product',
    contentName,
    contentCategory,
    numItems: facebookProperties.numItems || facebookProperties.num_items || contents.length || undefined,
    ...normalizePlatformProperties(facebookProperties)
  }

  if (eventType === 'CompleteRegistration') {
    delete customData.value
    delete customData.currency
    delete customData.contentIds
    delete customData.contentType
    delete customData.contentName
    delete customData.contentCategory
    delete customData.numItems
  }

  Object.keys(customData).forEach(key => {
    if (customData[key] === undefined) {
      delete customData[key]
    }
  })

  return Object.keys(customData).length > 0 ? customData : undefined
}

async function sendFacebookServerEvent(eventName, {
  eventId,
  contents = [],
  value,
  currency = 'USD',
  userData,
  facebookProperties = {}
} = {}) {
  if (!process.client || !['AddToCart', 'CompleteRegistration', 'Purchase'].includes(eventName)) {
    return
  }

  try {
    const isReady = await waitForFacebookReady()

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping server event:', eventName)
      return
    }

    const purchaseContext = buildFacebookPurchaseContext({
      eventName,
      eventId,
      userData,
      value,
      currency
    })
    const purchaseDedupKey = purchaseContext.dedupKey

    if (purchaseContext.isDuplicate) {
      console.warn('Facebook Purchase server event already sent, skipping duplicate:', purchaseDedupKey)
      return
    }

    const shopId = resolveFacebookShopId()
    const authorization = getToken() || getMallAppToken() || ''
    const serverUserData = await buildFacebookServerUserData(userData)
    const data = [{
      eventName,
      eventTime: Math.floor(Date.now() / 1000),
      eventId: eventId || generateEventId(),
      eventSourceUrl: buildFacebookEventSourceUrl(),
      actionSource: 'website',
      optOut: false,
      userData: serverUserData,
      customData: buildFacebookServerCustomData(eventName, contents, value, currency, facebookProperties),
      originalEventData: {
        eventTime: Math.floor(Date.now() / 1000),
        eventName
      }
    }]

    if (!data[0].userData) {
      delete data[0].userData
    }

    if (!data[0].customData) {
      delete data[0].customData
    }

    await useNuxtApp().$customFetch('/mall/api/mall/app/facebook/sendEvents', {
      method: 'POST',
      credentials: 'include',
      headers: {
        authorization,
        shopId
      },
      body: {
        param: {
          shopId,
          userId: resolveFacebookUserId(userData || {}),
          data
        }
      }
    })

    if (eventName === 'Purchase') {
      markFacebookPurchaseEventSent(purchaseDedupKey)
    }
  } catch (error) {
    console.warn('Facebook server tracking error:', error)
  }
}

/**
 * Enhanced trackEvent function supporting Google Analytics, TikTok Pixel, and Facebook Pixel
 * @param {string} eventType - Event type (ViewContent, AddToCart, etc.)
 * @param {Object} [options={}] - Event options
 * @param {Array} [options.contents] - Array of content objects for TikTok and Facebook
 * @param {number} [options.value] - Event value
 * @param {string} [options.currency="USD"] - Currency code (ISO 4217)
 * @param {string} [options.search_string] - Search keywords (for Search event)
 * @param {Object} [options.userData] - User identification data for TikTok and Facebook
 * @param {string} [options.eventId] - Event ID shared with pixel/server-side tracking
 * @param {string} [options.category] - GA category (for backward compatibility)
 * @param {string} [options.action] - GA action (for backward compatibility)
 * @param {string} [options.label] - GA label (for backward compatibility)
 */
export function trackEvent(eventType, options = {}) {
  const {
    contents = [],
    value,
    currency = "USD",
    search_string,
    userData,
    eventId,
    tikTokProperties = {},
    facebookProperties = {},
    // Backward compatibility for Google Analytics
    category,
    action,
    label
  } = options;

  // Handle user identification for TikTok and Facebook
  if (userData && Object.keys(userData).length > 0) {
    identifyTikTokUser(userData);
    identifyFacebookUser(userData);
  }

  // TikTok event mapping
  const tikTokEvents = {
    'ViewContent': 'ViewContent',
    'AddToWishlist': 'AddToWishlist',
    'Search': 'Search',
    'AddPaymentInfo': 'AddPaymentInfo',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'PlaceAnOrder': 'PlaceAnOrder',
    'CompleteRegistration': 'CompleteRegistration',
    'Purchase': 'Purchase'
  };

  // Facebook event mapping
  const facebookEvents = {
    'ViewContent': 'ViewContent',
    'AddToWishlist': 'AddToWishlist',
    'Search': 'Search',
    'AddPaymentInfo': 'AddPaymentInfo',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'CompleteRegistration': 'CompleteRegistration',
    'Purchase': 'Purchase', 
    'Lead': 'Lead',
    'Contact': 'Contact',
    'CustomizeProduct': 'CustomizeProduct',
    'Donate': 'Donate',
    'FindLocation': 'FindLocation',
    'Schedule': 'Schedule',
    'StartTrial': 'StartTrial',
    'SubmitApplication': 'SubmitApplication',
    'Subscribe': 'Subscribe'
  };

  const resolvedFacebookEventId = facebookEvents[eventType]
    ? (eventId || generateEventId())
    : eventId;
  const resolvedTikTokEventId = eventId;

  if (tikTokEvents[eventType]) {
    // Build TikTok parameters
    const tikTokParams = {
      contents: contents.map(content => ({
        content_id: content.content_id || content.id,
        content_type: content.content_type || 'product',
        content_name: content.content_name || content.name
      })),
      currency: currency,
      // TikTok requires value to be a number (integer or decimal) without currency symbols, letters, special characters, or commas
      // Value is required, so we always include it after normalization
      value: normalizeValue(value),
      ...normalizePlatformProperties(tikTokProperties)
    };

    // Add search_string for Search event
    if (eventType === 'Search' && search_string) {
      tikTokParams.search_string = search_string;
    }

    trackTikTokEvent(tikTokEvents[eventType], tikTokParams, {
      eventId: resolvedTikTokEventId
    });
  }

  if (facebookEvents[eventType]) {
    // Build Facebook parameters
    const facebookParams = {
      content_ids: contents.map(content => content.content_id || content.id),
      content_name: contents.length > 0 ? (contents[0].content_name || contents[0].name) : undefined,
      content_type: contents.length > 0 ? (contents[0].content_type || 'product') : 'product',
      currency: currency,
      // Normalize value to ensure it's a valid number
      value: normalizeValue(value),
      ...normalizePlatformProperties(facebookProperties)
    };

    // Add search_string for Search event
    if (eventType === 'Search' && search_string) {
      facebookParams.search_string = search_string;
    }

    // Remove undefined values
    Object.keys(facebookParams).forEach(key => {
      if (facebookParams[key] === undefined) {
        delete facebookParams[key];
      }
    });

    trackFacebookEvent(eventType, facebookParams, {
      eventId: resolvedFacebookEventId
    });

    void sendFacebookServerEvent(facebookEvents[eventType], {
      eventId: resolvedFacebookEventId,
      contents,
      value,
      currency,
      userData,
      facebookProperties
    })
  }

  // Backward compatibility with Google Analytics
  if (category && action) {
    trackGoogleAnalytics(category, action, label, value);
  }

  return {
    eventId: resolvedFacebookEventId || resolvedTikTokEventId
  };
}
/*

// Universal events (work with TikTok Pixel, Facebook Pixel, and Google Analytics)
trackEvent('ViewContent', {
  contents: [{ content_id: '123', content_name: 'Product Name' }],
  value: 99.99,
  currency: 'USD'
});

// AddToCart with user identification (supports both TikTok and Facebook)
trackEvent('AddToCart', {
  contents: [{ content_id: '123', content_name: 'Product Name' }],
  value: 99.99,
  currency: 'USD',
  userData: {
    email: 'hashed_email@example.com', // SHA-256 hashed
    phone_number: 'hashed_phone@example.com', // SHA-256 hashed (optional)
    first_name: 'hashed_first_name', // SHA-256 hashed (Facebook only)
    last_name: 'hashed_last_name' // SHA-256 hashed (Facebook only)
  }
});

// Search event
trackEvent('Search', {
  contents: [{ content_id: 'search_results', content_name: 'Search Results' }],
  value: 0,
  currency: 'USD',
  search_string: 'red shoes'
});

// Purchase event
trackEvent('Purchase', {
  contents: [
    { content_id: '123', content_name: 'Product A', content_type: 'product' },
    { content_id: '456', content_name: 'Product B', content_type: 'product' }
  ],
  value: 199.99,
  currency: 'USD'
});

// Lead/Contact event
trackEvent('Lead', {
  contents: [{ content_id: 'contact_form', content_name: 'Contact Form' }],
  value: 0,
  currency: 'USD'
});

// Backward compatibility with Google Analytics
trackEvent('ecommerce', {
  category: 'ecommerce',
  action: 'purchase',
  label: 'Product Purchase',
  value: 199.99
});
*/
